import { useEffect, useRef, useState } from "react"
import { Button, Form, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { APIService } from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import moment from "moment"
import IncidentFileGallery from "./IncidentFileGallery"

interface FileRecord { id: string; filePath: string; commentId?: string }
interface Comment {
  id: string
  incidentId: string
  accountId: string
  comment: string
  created: string
  author: { id: string; firstName: string; lastName: string } | null
  files: FileRecord[]
}

interface Props {
  incidentId: string
}

const IncidentCommentThread = ({ incidentId }: Props) => {
  const { t } = useTranslation("global")
  const user: any = parsedUser()
  const [comments, setComments] = useState<Comment[]>([])
  const [text, setText] = useState("")
  const [pendingFiles, setPendingFiles] = useState<{ localUri: string; filePath: string | null; uploading: boolean }[]>([])
  const [isSending, setIsSending] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadComments = async () => {
    const res = await APIService.getAction("Incident/Comments/List", `incidentId=${incidentId}`)
    if (res.data.status === "ok") setComments(res.data.response)
  }

  useEffect(() => { loadComments() }, [incidentId])

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !file.type.match(/image.*/)) return
    const tmp = { localUri: URL.createObjectURL(file), filePath: null as string | null, uploading: true }
    setPendingFiles(prev => [...prev, tmp])
    try {
      const formData = new FormData()
      formData.append("path", file)
      const res = await APIService.postAction("File/Upload", formData)
      const filePath = res.data?.response ?? null
      setPendingFiles(prev => prev.map(f => f === tmp ? { ...f, filePath, uploading: false } : f))
    } catch {
      setPendingFiles(prev => prev.filter(f => f !== tmp))
    }
    e.target.value = ""
  }

  const handleSend = async () => {
    if (!text.trim()) return
    setIsSending(true)
    try {
      const res = await APIService.postAction("Incident/Comments/Add", {
        incidentId,
        accountId: user.ID,
        comment: text.trim(),
        createdId: user.ID,
      })
      if (res.data.status !== "ok") return
      const commentId = res.data.response.id

      const uploaded = pendingFiles.filter(f => f.filePath)
      await Promise.all(uploaded.map(f =>
        APIService.postAction("Incident/Files/Add", {
          incidentId,
          commentId,
          filePath: f.filePath,
          createdId: user.ID,
        })
      ))

      setText("")
      setPendingFiles([])
      await loadComments()
      toast.success(t("record saved"))
    } catch {
      toast.error(t("Error"))
    } finally {
      setIsSending(false)
    }
  }

  const handleCommentFileDeleted = (commentId: string, fileId: string) => {
    setComments(prev => prev.map(c =>
      c.id === commentId
        ? { ...c, files: c.files.filter(f => f.id !== fileId) }
        : c
    ))
  }

  return (
    <div className="d-flex flex-column h-100">
      <h6 className="fw-semibold mb-3 border-bottom pb-2">{t("Comentarios internos")}</h6>

      <div className="flex-grow-1 overflow-auto mb-3" style={{ maxHeight: 420 }}>
        {comments.length === 0 && (
          <p className="text-muted small text-center py-4">{t("Sin comentarios")}</p>
        )}
        {comments.map(c => (
          <div key={c.id} className="mb-3 p-2 bg-light rounded">
            <div className="d-flex justify-content-between mb-1">
              <strong className="small">
                {c.author ? `${c.author.firstName} ${c.author.lastName}` : c.accountId}
              </strong>
              <span className="text-muted" style={{ fontSize: 11 }}>
                {moment(c.created).format("DD/MM/YY HH:mm")}
              </span>
            </div>
            <p className="mb-1 small" style={{ whiteSpace: "pre-wrap" }}>{c.comment}</p>
            {c.files.length > 0 && (
              <IncidentFileGallery
                files={c.files}
                canDelete
                onDeleted={id => handleCommentFileDeleted(c.id, id)}
              />
            )}
          </div>
        ))}
      </div>

      <div>
        <Form.Control
          as="textarea"
          rows={3}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={t("Comentarios internos") + "..."}
          className="mb-2"
        />

        {pendingFiles.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mb-2">
            {pendingFiles.map((f, i) => (
              <div key={i} className="position-relative">
                <img
                  src={f.localUri}
                  alt=""
                  style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 4, opacity: f.uploading ? 0.5 : 1 }}
                />
                {f.uploading && (
                  <Spinner animation="border" size="sm" className="position-absolute top-50 start-50 translate-middle" />
                )}
                {!f.uploading && (
                  <button
                    className="btn btn-danger btn-sm position-absolute top-0 end-0 p-0 px-1"
                    style={{ fontSize: 10 }}
                    onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}
                  >×</button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
            <i className="fas fa-paperclip me-1" />{t("Adjuntar archivo")}
          </Button>
          <Button variant="primary" size="sm" onClick={handleSend} disabled={isSending || !text.trim()}>
            {isSending ? <Spinner animation="border" size="sm" /> : t("Enviar comentario")}
          </Button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png"
          className="d-none"
          onChange={handleFileSelect}
        />
      </div>
    </div>
  )
}

export default IncidentCommentThread
